import {
  type AutocompleteGroupedOption,
  type Theme,
  createFilterOptions,
  useAutocomplete,
  useMediaQuery,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface UsePlainAutocompleteProps<T extends object> {
  value: T[];
  options: T[];
  onChange: (value: T[]) => void;
  groupBy?: (option: T) => string;
  getOptionLabel: (option: T) => string;
  getOptionKey?: (option: T) => string | number;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
}

export type GroupedOption<T> = AutocompleteGroupedOption<T>;

export const usePlainAutocomplete = <T extends object>({
  value,
  options,
  onChange,
  groupBy,
  getOptionLabel,
  getOptionKey = getOptionLabel,
  isOptionEqualToValue = (a, b) => a === b,
}: UsePlainAutocompleteProps<T>) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("desktop"));
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [displayAllOptions, setDisplayAllOptions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const filter = useMemo(
    () =>
      createFilterOptions<T>({
        matchFrom: "any",
        ignoreCase: true,
        trim: true,
        stringify: (option) => (groupBy ? `${getOptionLabel(option)} ${groupBy(option)}` : getOptionLabel(option)),
      }),
    [getOptionLabel, groupBy]
  );

  const filteredOptions = useMemo(
    () => filter([...options], { inputValue, getOptionLabel }),
    [filter, options, inputValue, getOptionLabel]
  );

  const { groupedOptions, getInputProps } = useAutocomplete({
    id: "plain-autocomplete",
    options,
    value,
    multiple: true,
    getOptionLabel,
    isOptionEqualToValue,
    ...(groupBy && { groupBy }),
    open: true,
    filterOptions: filter,
    disableCloseOnSelect: true,
    clearOnBlur: false,
    inputValue,
    onInputChange: (_, newInputValue) => setInputValue(newInputValue),
  });

  const hasGroups = groupBy !== undefined && groupedOptions.length > 0 && "group" in groupedOptions[0];

  const normalizedGroupedOptions = useMemo((): GroupedOption<T>[] => {
    if (hasGroups) {
      return groupedOptions as GroupedOption<T>[];
    }

    const flatOptions = groupedOptions as T[];
    if (flatOptions.length === 0) {
      return [];
    }
    return [{ group: "", index: 0, key: 0, options: flatOptions }];
  }, [groupedOptions, hasGroups]);

  useEffect(() => {
    if (normalizedGroupedOptions.length > 0) {
      setOpenGroups((prev) => {
        const newState = { ...prev };
        let hasChanges = false;
        for (const group of normalizedGroupedOptions) {
          if (!(group.group in newState)) {
            newState[group.group] = true;
            hasChanges = true;
          }
        }
        return hasChanges ? newState : prev;
      });
    }
  }, [normalizedGroupedOptions]);

  const customOnChange = useCallback(
    (newValue: T[]) => {
      onChange(newValue);
    },
    [onChange]
  );

  const isAllSelected = useMemo(() => {
    return (
      filteredOptions.length > 0 && filteredOptions.every((opt) => value.some((v) => isOptionEqualToValue(opt, v)))
    );
  }, [filteredOptions, value, isOptionEqualToValue]);

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      customOnChange(value.filter((v) => !filteredOptions.some((o) => isOptionEqualToValue(o, v))));
    } else {
      const newVals = [
        ...value.filter((v) => !filteredOptions.some((o) => isOptionEqualToValue(o, v))),
        ...filteredOptions,
      ];
      customOnChange(newVals);
    }
  }, [filteredOptions, isAllSelected, customOnChange, value, isOptionEqualToValue]);

  const isGroupSelected = useCallback(
    (groupOptions: T[]) =>
      groupOptions.length > 0 && groupOptions.every((opt) => value.some((v) => isOptionEqualToValue(opt, v))),
    [value, isOptionEqualToValue]
  );

  const isGroupIndeterminate = useCallback(
    (groupOptions: T[]) =>
      groupOptions.some((opt) => value.some((v) => isOptionEqualToValue(opt, v))) && !isGroupSelected(groupOptions),
    [isGroupSelected, value, isOptionEqualToValue]
  );

  const handleSelectGroup = useCallback(
    (groupOptions: T[]) => {
      if (isGroupSelected(groupOptions)) {
        customOnChange(value.filter((v) => !groupOptions.some((o) => isOptionEqualToValue(o, v))));
      } else {
        const newVals = [
          ...value.filter((v) => !groupOptions.some((o) => isOptionEqualToValue(o, v))),
          ...groupOptions,
        ];
        customOnChange(newVals);
      }
    },
    [isGroupSelected, customOnChange, value, isOptionEqualToValue]
  );

  const isOptionSelected = useCallback(
    (opt: T) => value.some((v) => isOptionEqualToValue(opt, v)),
    [value, isOptionEqualToValue]
  );

  const handleSelectOption = useCallback(
    (opt: T) => {
      if (isOptionSelected(opt)) {
        customOnChange(value.filter((v) => !isOptionEqualToValue(opt, v)));
      } else {
        customOnChange([...value, opt]);
      }
    },
    [isOptionSelected, customOnChange, value, isOptionEqualToValue]
  );

  const totalOptionsCount = useMemo(() => {
    return normalizedGroupedOptions.reduce((total, group) => total + group.options.length, 0);
  }, [normalizedGroupedOptions]);

  return {
    filteredOptions,
    inputValue,
    setInputValue,
    openGroups,
    setOpenGroups,
    groupedOptions: normalizedGroupedOptions,
    hasGroups,
    isAllSelected,
    isDesktop,
    isGroupSelected,
    isGroupIndeterminate,
    isOptionSelected,
    handleSelectAll,
    handleSelectGroup,
    handleSelectOption,
    totalOptionsCount,
    displayAllOptions,
    setDisplayAllOptions,
    getOptionLabel,
    getOptionKey,
    getInputProps,
  };
};
